import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import EditorJS from '@editorjs/editorjs';
import List from '@editorjs/list';
import NestedList from '@editorjs/nested-list';
import Checklist from '@editorjs/checklist';
import Quote from '@editorjs/quote';
import Delimiter from '@editorjs/delimiter';
import Marker from '@editorjs/marker';
import InlineCode from '@editorjs/inline-code';
import LinkTool from '@editorjs/link';
import Table from '@editorjs/table';
import Warning from '@editorjs/warning';
import CodeTool from '@editorjs/code';
import RawTool from '@editorjs/raw';
import Underline from '@editorjs/underline';
import TextVariantTune from '@editorjs/text-variant-tune';

// Custom blocks
import RichParagraph from './blocks/RichParagraph';
import RichHeader from './blocks/RichHeader';
import {
  createTextColorInlineTool,
  createInlineCommentTool,
} from './blocks/editorInlineTools';
import CalloutBlock from './blocks/CalloutBlock';
import CodeBlockWithHighlight from './blocks/CodeBlockWithHighlight';
import MermaidBlock from './blocks/MermaidBlock';
import ApiEndpointBlock from './blocks/ApiEndpointBlock';
import TabsBlock from './blocks/TabsBlock';
import AlertBlock from './blocks/AlertBlock';
import AccordionBlock from './blocks/AccordionBlock';
import ButtonBlock from './blocks/ButtonBlock';
import DividerBlock from './blocks/DividerBlock';
import ImageBlock from './blocks/ImageBlock';
import VideoBlock from './blocks/VideoBlock';
import FileBlock from './blocks/FileBlock';
import EmbedBlock from './blocks/EmbedBlock';
import ToggleBlock from './blocks/ToggleBlock';
import ColumnBlock from './blocks/ColumnBlock';
import CardBlock from './blocks/CardBlock';
import StepsBlock from './blocks/StepsBlock';
import ParameterBlock from './blocks/ParameterBlock';
import BadgeBlock from './blocks/BadgeBlock';
import TooltipBlock from './blocks/TooltipBlock';

// Modals
import DocumentInfoModal from './DocumentInfoModal';
import PingTeamModal from './PingTeamModal';
import ShareDocumentModal from './ShareDocumentModal';
import FindReplaceModal from './FindReplaceModal';
import ExportDocumentModal from './ExportDocumentModal';
import EditorAiAssistantModal from './EditorAiAssistantModal';
import InlineCommentsPanel from './InlineCommentsPanel';
import { editorDataToPlainText, plainTextToParagraphBlocks, plainTextToParagraphHtml } from '../../lib/editorAi';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Save,
  Loader2,
  Check,
  MoreVertical,
  Info,
  Bell,
  Share2,
  Search,
  Download,
  Maximize2,
  Minimize2,
  Eye,
  Edit3,
  MessageSquare,
  Palette,
  X,
  Sparkles,
  Wand2,
  ChevronDown,
  SpellCheck2,
  Minus,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { wrapSelectionWithCommentMark, unwrapCommentMarkSpan } from '../../lib/inlineCommentMark';
import { API_URL } from '../../config';

// Store active editor instances globally to prevent duplicates
const activeEditors = new Map();

// Color picker component
const ColorPicker = ({ onColorSelect, onClose, closeOnPick = true }) => {
  const colors = [
    '#000000', '#374151', '#6B7280', '#9CA3AF',
    '#EF4444', '#F97316', '#F59E0B', '#EAB308',
    '#22C55E', '#10B981', '#14B8A6', '#06B6D4',
    '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7',
    '#EC4899', '#F43F5E', '#ffffff', 'transparent',
  ];

  return (
    <div className="p-2" data-testid="color-picker">
      <div className="grid grid-cols-5 gap-1 mb-2">
        {colors.map((color) => (
          <button
            key={color}
            onClick={() => {
              onColorSelect(color);
              if (closeOnPick) onClose?.();
            }}
            className={cn(
              "w-6 h-6 rounded border border-slate-300 dark:border-slate-600 hover:scale-110 transition-transform",
              color === 'transparent' && "bg-[repeating-conic-gradient(#ccc_0%_25%,transparent_0%_50%)] bg-[length:8px_8px]",
              color === '#ffffff' && "bg-white"
            )}
            style={color !== 'transparent' ? { backgroundColor: color } : undefined}
            data-testid={`color-${color}`}
          />
        ))}
      </div>
      <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
        <label className="text-xs text-slate-500 mb-1 block">Custom color</label>
        <input
          type="color"
          className="w-full h-8 cursor-pointer rounded"
          onChange={(e) => {
            onColorSelect(e.target.value);
            if (closeOnPick) onClose?.();
          }}
          data-testid="custom-color-input"
        />
      </div>
    </div>
  );
};

const BlockEditor = ({
  document,
  onUpdate,
  readOnly: propReadOnly = false,
  space,
  members = [],
  publishSettings,
  currentOrg,
  currentUser,
  onPublishChange,
  onSpaceSettingsChange,
  onFullWidthChange,
}) => {
  const holderId = useMemo(() => `editorjs-${document?.id || 'new'}`, [document?.id]);
  const editorInstanceRef = useRef(null);
  const titleInputRef = useRef(null);
  const [title, setTitle] = useState(document?.title || '');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  // New state for enhanced features
  const [isReadMode, setIsReadMode] = useState(false);
  const [isFullWidth, setIsFullWidth] = useState(space?.settings?.full_width || false);
  const [showFeedbackPanel, setShowFeedbackPanel] = useState(
    document?.show_feedback_panel !== false
  );
  
  // Modal states
  const [showDocInfo, setShowDocInfo] = useState(false);
  const [showPingTeam, setShowPingTeam] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showAiAssistant, setShowAiAssistant] = useState(false);
  /** @type {'write' | 'summary' | 'grammar' | 'shorten'} */
  const [aiModalMode, setAiModalMode] = useState('write');
  const [showComments, setShowComments] = useState(false);
  /** Inline mark click → open sidebar on this thread */
  const [focusedSidebarCommentId, setFocusedSidebarCommentId] = useState(null);

  
  // Inline comments
  const [inlineComments, setInlineComments] = useState([]);
  const [selectedText, setSelectedText] = useState('');
  /** Inline toolbar popover: color picker or comment form */
  const [inlineExtraPanel, setInlineExtraPanel] = useState(null);
  const [newCommentText, setNewCommentText] = useState('');
  const savedInlineRangeRef = useRef(null);
  const inlineHandlersRef = useRef({});

  const TextColorInlineToolClass = useMemo(
    () => createTextColorInlineTool(() => inlineHandlersRef.current),
    []
  );
  const InlineCommentToolClass = useMemo(
    () => createInlineCommentTool(() => inlineHandlersRef.current),
    []
  );

  // Effective readOnly state
  const readOnly = propReadOnly || isReadMode;

  const closeInlineExtraPanel = useCallback(() => {
    setInlineExtraPanel(null);
    setNewCommentText('');
    setSelectedText('');
    savedInlineRangeRef.current = null;
  }, []);

  const restoreInlineSelection = useCallback(() => {
    const r = savedInlineRangeRef.current;
    if (!r || r.collapsed) return false;
    const root = window.document.getElementById(holderId);
    if (!root?.contains(r.commonAncestorContainer)) return false;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(r.cloneRange());
    return true;
  }, [holderId]);

  // Sync full width with space settings
  useEffect(() => {
    setIsFullWidth(space?.settings?.full_width || false);
  }, [space?.settings?.full_width]);

  useEffect(() => {
    setShowFeedbackPanel(document?.show_feedback_panel !== false);
  }, [document?.id, document?.show_feedback_panel]);

  // Handle full width toggle
  const handleFullWidthToggle = useCallback(async (checked) => {
    setIsFullWidth(checked);
    
    // Notify parent to collapse/expand sidebar
    if (onFullWidthChange) {
      onFullWidthChange(checked);
    }
    
    // Persist to space settings
    if (onSpaceSettingsChange) {
      try {
        await onSpaceSettingsChange({ full_width: checked });
        toast.success(checked ? 'Full width enabled' : 'Limited width enabled');
      } catch (error) {
        toast.error('Failed to update settings');
        setIsFullWidth(!checked);
      }
    }
  }, [onSpaceSettingsChange, onFullWidthChange]);

  const handleShowFeedbackToggle = useCallback(async (checked) => {
    if (!onUpdate || !document?.id) return;
    setShowFeedbackPanel(checked);
    try {
      await onUpdate(document.id, { show_feedback_panel: checked });
      toast.success(checked ? 'Feedback panel enabled' : 'Feedback panel hidden');
    } catch (error) {
      setShowFeedbackPanel(!checked);
      toast.error('Failed to update feedback setting');
    }
  }, [document?.id, onUpdate]);

  // Update title when document changes (new document selected)
  useEffect(() => {
    setTitle(document?.title || '');
    setHasChanges(false);
  }, [document?.id]);

  // Fetch inline comments
  useEffect(() => {
    if (!document?.id) return;
    
    const fetchComments = async () => {
      try {
        const response = await axios.get(`${API_URL}/documents/${document.id}/inline-comments`);
        setInlineComments(response.data);
      } catch (error) {
        // Silently fail if endpoint doesn't exist yet
        console.log('Inline comments not available');
      }
    };
    
    fetchComments();
  }, [document?.id]);

  // Track title changes
  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    setHasChanges(true);
  };

  // Parse content - handle both string and object formats
  const parseContent = useCallback((content) => {
    if (!content) {
      return { time: Date.now(), blocks: [], version: '2.28.0' };
    }
    if (typeof content === 'string') {
      try {
        return JSON.parse(content);
      } catch {
        return {
          time: Date.now(),
          blocks: content ? [{ type: 'paragraph', data: { text: content } }] : [],
          version: '2.28.0'
        };
      }
    }
    return content;
  }, []);

  // Save function
  const handleSave = useCallback(async (editorData = null, currentTitle = null) => {
    if (!onUpdate || saving) return;

    setSaving(true);
    try {
      let contentToSave = editorData;
      if (!contentToSave && editorInstanceRef.current) {
        contentToSave = await editorInstanceRef.current.save();
      }
      
      const titleToSave = currentTitle !== null ? currentTitle : title;
      
      await onUpdate(document.id, {
        title: titleToSave,
        content: JSON.stringify(contentToSave)
      });
      setHasChanges(false);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setSaving(false);
    }
  }, [document?.id, onUpdate, saving, title]);

  // Handle find/replace
  const handleFindReplace = useCallback(async (newContent, count) => {
    if (!document?.id) return;
    
    try {
      await onUpdate(document.id, { content: newContent });
      toast.success(`Replaced ${count} occurrence(s)`);
      
      // Re-initialize editor with new content
      const editor = activeEditors.get(holderId);
      if (editor) {
        const parsedContent = parseContent(newContent);
        await editor.render(parsedContent);
      }
    } catch (error) {
      toast.error('Failed to replace');
    }
  }, [document?.id, onUpdate, holderId, parseContent]);

  const getFullDocumentPlainText = useCallback(async () => {
    const ed = editorInstanceRef.current;
    if (!ed) return '';
    const data = await ed.save();
    return editorDataToPlainText(data);
  }, []);

  const getSelectionPlainText = useCallback(() => {
    const root = window.document.getElementById(holderId);
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !root) return '';
    const r = sel.getRangeAt(0);
    if (!root.contains(r.commonAncestorContainer)) return '';
    return sel.toString();
  }, [holderId]);

  const insertAiPlainTextAtEnd = useCallback(
    async (plainOutput) => {
      const editor = activeEditors.get(holderId) || editorInstanceRef.current;
      if (!editor) {
        toast.error('Editor not ready');
        throw new Error('Editor not ready');
      }
      const blocks = plainTextToParagraphBlocks(plainOutput);
      if (blocks.length === 0) {
        toast.error('Nothing to insert');
        return;
      }
      let saved = await editor.save();
      let index = saved.blocks.length;
      for (const b of blocks) {
        editor.blocks.insert(b.type, b.data, {}, index, false);
        index += 1;
      }
      setHasChanges(true);
      toast.success('AI result inserted at the end');
    },
    [holderId]
  );

  const insertAiSummaryCallout = useCallback(
    async (plainOutput) => {
      const editor = activeEditors.get(holderId) || editorInstanceRef.current;
      if (!editor) {
        toast.error('Editor not ready');
        throw new Error('Editor not ready');
      }
      const messageHtml = plainTextToParagraphHtml(plainOutput);
      const attribution = plainTextToParagraphHtml('— AI Generated');
      let saved = await editor.save();
      let index = saved.blocks.length;
      editor.blocks.insert(
        'callout',
        {
          type: 'info',
          title: 'Summary',
          message: messageHtml,
        },
        {},
        index,
        false
      );
      index += 1;
      editor.blocks.insert(
        'paragraph',
        {
          text: `<span style="color:#94a3b8;font-size:0.875rem">${attribution}</span>`,
        },
        {},
        index,
        false
      );
      setHasChanges(true);
      toast.success('Summary block inserted');
    },
    [holderId]
  );

  const openAiModal = useCallback((nextMode) => {
    setAiModalMode(nextMode);
    setShowAiAssistant(true);
  }, []);

  // Prefer <span style="color:.."> (persists through Editor.js save); fallback foreColor + styleWithCSS
  const applyTextColor = useCallback((color) => {
    const editorContainer = window.document.getElementById(holderId);
    if (!editorContainer) return;

    if (savedInlineRangeRef.current) {
      restoreInlineSelection();
    }

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      toast.error('Please select some text first');
      return;
    }

    const liveRange = selection.getRangeAt(0);
    if (!editorContainer.contains(liveRange.commonAncestorContainer)) {
      toast.error('Please select text within the editor');
      return;
    }

    try {
      let applied = false;

      if (color === 'transparent') {
        try {
          window.document.execCommand('styleWithCSS', false, 'true');
        } catch (_) {
          /* ignore */
        }
        applied = window.document.execCommand('foreColor', false, 'inherit');
      } else {
        const span = window.document.createElement('span');
        span.style.color = color;
        try {
          liveRange.surroundContents(span);
          applied = true;
          selection.removeAllRanges();
          const nr = window.document.createRange();
          nr.selectNodeContents(span);
          nr.collapse(false);
          selection.addRange(nr);
        } catch {
          if (savedInlineRangeRef.current) {
            restoreInlineSelection();
          }
          try {
            window.document.execCommand('styleWithCSS', false, 'true');
          } catch (_) {
            /* ignore */
          }
          const sel2 = window.getSelection();
          applied =
            !!sel2 &&
            sel2.rangeCount > 0 &&
            !sel2.isCollapsed &&
            window.document.execCommand('foreColor', false, color);
        }
      }

      if (!applied) {
        toast.error('Could not apply color to this selection');
        return;
      }

      setHasChanges(true);
      toast.success('Color applied — save to persist');
      closeInlineExtraPanel();
    } catch (e) {
      console.error('Color application failed:', e);
      toast.error('Could not apply color');
    }
  }, [holderId, restoreInlineSelection, closeInlineExtraPanel]);

  // Add inline comment (wrap selection in document HTML, then persist)
  const addInlineComment = useCallback(async () => {
    if (!selectedText || !newCommentText.trim() || !document?.id) return;

    const editorEl = window.document.getElementById(holderId);
    if (!editorEl) {
      toast.error('Editor not ready');
      return;
    }

    if (savedInlineRangeRef.current) {
      restoreInlineSelection();
    }

    const commentId = crypto.randomUUID();
    const wrapResult = wrapSelectionWithCommentMark(editorEl, commentId);
    if (!wrapResult.ok) {
      toast.error(wrapResult.error || 'Could not attach comment to selection');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/documents/${document.id}/inline-comments`, {
        id: commentId,
        selected_text: selectedText,
        text: newCommentText.trim(),
        author_id: currentUser?.id,
        author_name: currentUser?.name || currentUser?.email || 'Unknown',
      });

      setInlineComments((prev) => [...prev, response.data]);
      closeInlineExtraPanel();
      window.getSelection()?.removeAllRanges();
      setHasChanges(true);
      toast.success('Comment added — save the document to keep it');
    } catch (error) {
      editorEl.querySelectorAll(`span.doc-inline-comment-mark[data-doc-comment-id="${commentId}"]`).forEach(unwrapCommentMarkSpan);
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    }
  }, [selectedText, newCommentText, document?.id, currentUser, holderId, restoreInlineSelection, closeInlineExtraPanel]);

  const handleCommentDeletedFromEditor = useCallback(
    (commentId) => {
      const root = window.document.getElementById(holderId);
      if (root) {
        root
          .querySelectorAll(`span.doc-inline-comment-mark[data-doc-comment-id="${commentId}"]`)
          .forEach(unwrapCommentMarkSpan);
      }
      setFocusedSidebarCommentId((id) => (id === commentId ? null : id));
      setHasChanges(true);
    },
    [holderId]
  );

  useEffect(() => {
    if (!inlineExtraPanel) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeInlineExtraPanel();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [inlineExtraPanel, closeInlineExtraPanel]);

  const highlightCommentText = useCallback((commentId, highlight) => {
    const root = window.document.getElementById(holderId);
    if (!root || !commentId) return;
    const el = root.querySelector(`span.doc-inline-comment-mark[data-doc-comment-id="${commentId}"]`);
    if (!el) return;
    if (highlight) {
      el.classList.add('doc-inline-comment-mark--highlight');
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } else {
      el.classList.remove('doc-inline-comment-mark--highlight');
    }
  }, [holderId]);

  const openCommentsFromMark = useCallback(
    (commentId) => {
      setFocusedSidebarCommentId((prev) => {
        if (prev && prev !== commentId) {
          highlightCommentText(prev, false);
        }
        return commentId;
      });
      setShowComments(true);
      highlightCommentText(commentId, true);
      window.getSelection()?.removeAllRanges();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.document
            .querySelector(`[data-inline-comment-card="${commentId}"]`)
            ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });
      });
    },
    [highlightCommentText]
  );

  const closeCommentsPanel = useCallback(() => {
    if (focusedSidebarCommentId) {
      highlightCommentText(focusedSidebarCommentId, false);
    }
    setFocusedSidebarCommentId(null);
    setShowComments(false);
  }, [focusedSidebarCommentId, highlightCommentText]);

  useEffect(() => {
    const root = window.document.getElementById(holderId);
    if (!root || readOnly) return undefined;

    const onMarkClick = (e) => {
      const mark = e.target?.closest?.('.doc-inline-comment-mark');
      if (!mark || !root.contains(mark)) return;
      const commentId = mark.getAttribute('data-doc-comment-id');
      if (!commentId) return;

      const inInlineToolbar = e.target?.closest?.('.ce-inline-toolbar');
      if (inInlineToolbar) return;

      e.preventDefault();
      e.stopPropagation();
      openCommentsFromMark(commentId);
    };

    root.addEventListener('click', onMarkClick, true);
    return () => root.removeEventListener('click', onMarkClick, true);
  }, [holderId, readOnly, openCommentsFromMark]);

  // Editor tools configuration (mediaUpload enables Image / Video / File local uploads)
  const editorTools = useMemo(() => {
    const mediaUpload = { spaceId: space?.id || '' };
    return {
    header: {
      class: RichHeader,
      inlineToolbar: true,
      config: {
        placeholder: 'Enter a heading',
        levels: [1, 2, 3, 4, 5, 6],
        defaultLevel: 2
      },
      shortcut: 'CMD+SHIFT+H'
    },
    paragraph: {
      class: RichParagraph,
      inlineToolbar: true,
    },
    list: {
      class: List,
      inlineToolbar: true,
      config: { defaultStyle: 'unordered' }
    },
    nestedList: {
      class: NestedList,
      inlineToolbar: true,
      config: { defaultStyle: 'unordered' }
    },
    checklist: { class: Checklist, inlineToolbar: true },
    marker: { class: Marker, shortcut: 'CMD+SHIFT+M' },
    inlineCode: { class: InlineCode, shortcut: 'CMD+SHIFT+C' },
    underline: Underline,
    textColor: { class: TextColorInlineToolClass },
    inlineComment: { class: InlineCommentToolClass },
    textVariant: TextVariantTune,
    quote: {
      class: Quote,
      inlineToolbar: true,
      config: {
        quotePlaceholder: 'Enter a quote',
        captionPlaceholder: 'Quote author'
      }
    },
    delimiter: Delimiter,
    linkTool: { class: LinkTool, config: { endpoint: '/api/fetchUrl' } },
    table: {
      class: Table,
      inlineToolbar: true,
      config: { rows: 2, cols: 3 }
    },
    code: CodeTool,
    codeHighlight: {
      class: CodeBlockWithHighlight,
      config: { placeholder: 'Enter code...' }
    },
    raw: { class: RawTool, config: { placeholder: 'Enter raw HTML' } },
    warning: {
      class: Warning,
      inlineToolbar: true,
      config: { titlePlaceholder: 'Title', messagePlaceholder: 'Message' }
    },
    callout: {
      class: CalloutBlock,
      inlineToolbar: true,
      config: { types: ['info', 'warning', 'error', 'success', 'note'] }
    },
    alert: { class: AlertBlock, inlineToolbar: true },
    mermaid: {
      class: MermaidBlock,
      config: { placeholder: 'Enter Mermaid diagram code...' }
    },
    apiEndpoint: {
      class: ApiEndpointBlock,
      config: { methods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] }
    },
    tabs: { class: TabsBlock, config: { placeholder: 'Tab content...' } },
    accordion: { class: AccordionBlock, inlineToolbar: true },
    toggle: { class: ToggleBlock, inlineToolbar: true },
    button: {
      class: ButtonBlock,
      config: { styles: ['primary', 'secondary', 'outline', 'ghost'] }
    },
    divider: {
      class: DividerBlock,
      config: { styles: ['solid', 'dashed', 'dotted', 'double'] }
    },
    image: { class: ImageBlock, config: { placeholder: 'Paste image URL...', mediaUpload } },
    video: {
      class: VideoBlock,
      config: { placeholder: 'Paste video URL (YouTube, Vimeo, or file)...', mediaUpload }
    },
    file: { class: FileBlock, config: { placeholder: 'Paste file URL...', mediaUpload } },
    embed: { class: EmbedBlock, config: { placeholder: 'Paste embed URL...' } },
    columns: { class: ColumnBlock, config: { columnCount: 2 } },
    card: { class: CardBlock, inlineToolbar: true },
    steps: { class: StepsBlock, config: { placeholder: 'Step description...' } },
    parameter: {
      class: ParameterBlock,
      config: { types: ['string', 'number', 'boolean', 'object', 'array'] }
    },
    badge: {
      class: BadgeBlock,
      config: { colors: ['blue', 'green', 'yellow', 'red', 'purple', 'gray'] }
    },
    tooltip: { class: TooltipBlock, inlineToolbar: true }
    };
  }, [space?.id, TextColorInlineToolClass, InlineCommentToolClass]);

  // Initialize Editor.js — never reuse a global Editor instance for a fresh mount (stale blocks / wrong doc).
  useEffect(() => {
    let isMounted = true;
    const hid = holderId;

    // Tear down any leftover instance for this holder (e.g. failed unmount, StrictMode, or remount).
    const stale = activeEditors.get(hid);
    if (stale) {
      try {
        stale.destroy();
      } catch (e) {
        // ignore
      }
      activeEditors.delete(hid);
    }
    editorInstanceRef.current = null;

    const initEditor = async () => {
      await new Promise(resolve => setTimeout(resolve, 100));

      const holderEl = window.document.getElementById(hid);
      if (!holderEl || !isMounted) return;

      try {
        const parsedContent = parseContent(document?.content);

        const editor = new EditorJS({
          holder: hid,
          readOnly,
          placeholder: 'Press Tab or click + to add a block...',
          data: parsedContent,
          autofocus: !readOnly,
          minHeight: 100,
          onChange: async () => {
            if (!readOnly && isMounted) {
              setHasChanges(true);
            }
          },
          tools: editorTools,
          tunes: ['textVariant']
        });

        await editor.isReady;

        if (!isMounted) {
          try {
            editor.destroy();
          } catch (e) {
            // ignore
          }
          return;
        }
        editorInstanceRef.current = editor;
        activeEditors.set(hid, editor);
      } catch (error) {
        console.error('Editor initialization failed:', error);
      }
    };

    initEditor();

    return () => {
      isMounted = false;
      const editor = activeEditors.get(hid);
      if (editor) {
        try {
          editor.destroy();
        } catch (e) {
          // Ignore
        }
        activeEditors.delete(hid);
      }
      editorInstanceRef.current = null;
    };
  }, [holderId, document?.id, readOnly, parseContent, editorTools, document?.content]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave(null, title);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        setShowFindReplace(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, title]);

  inlineHandlersRef.current = {
    onOpenColor: (range) => {
      if (readOnly || !range || range.collapsed) return;
      const t = range.toString().trim();
      if (!t) return;
      savedInlineRangeRef.current = range.cloneRange();
      setSelectedText(t);
      const rect = range.getBoundingClientRect();
      const panelW = 300;
      const pad = 8;
      let left = rect.left + rect.width / 2 - panelW / 2;
      left = Math.max(pad, Math.min(left, window.innerWidth - panelW - pad));
      let top = rect.bottom + pad;
      if (top + 260 > window.innerHeight) {
        top = Math.max(pad, rect.top - 260);
      }
      setInlineExtraPanel({ mode: 'color', top, left });
    },
    onOpenComment: (range) => {
      if (readOnly || !range || range.collapsed) return;
      const t = range.toString().trim();
      if (!t) return;
      savedInlineRangeRef.current = range.cloneRange();
      setSelectedText(t);
      const rect = range.getBoundingClientRect();
      const panelW = 320;
      const pad = 8;
      let left = rect.left + rect.width / 2 - panelW / 2;
      left = Math.max(pad, Math.min(left, window.innerWidth - panelW - pad));
      let top = rect.bottom + pad;
      if (top + 300 > window.innerHeight) {
        top = Math.max(pad, rect.top - 300);
      }
      setInlineExtraPanel({ mode: 'comment', top, left });
    },
  };

  if (!document) {
    return null;
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-slate-900" data-testid="block-editor">
      {/* Header with Read/Edit Toggle and 3-dot Menu */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 dark:border-slate-800">
        {/* Left: Read/Edit Toggle */}
        <div className="flex items-center gap-2">
          {!propReadOnly && (
            <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-1" data-testid="read-edit-toggle">
              <button
                onClick={() => setIsReadMode(false)}
                className={cn(
                  "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                  !isReadMode
                    ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
                )}
                data-testid="edit-mode-button"
              >
                <Edit3 className="w-4 h-4 inline mr-1" />
                Edit
              </button>
              <button
                onClick={() => setIsReadMode(true)}
                className={cn(
                  "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                  isReadMode
                    ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
                )}
                data-testid="read-mode-button"
              >
                <Eye className="w-4 h-4 inline mr-1" />
                Read
              </button>
            </div>
          )}
          
          {/* 3-dot Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="editor-menu-button">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuItem onClick={() => setShowDocInfo(true)} data-testid="menu-doc-info">
                <Info className="w-4 h-4 mr-2" />
                Document info
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowPingTeam(true)} data-testid="menu-ping-team">
                <Bell className="w-4 h-4 mr-2" />
                Ping team member
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowShare(true)} data-testid="menu-share">
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowFindReplace(true)} data-testid="menu-find-replace">
                <Search className="w-4 h-4 mr-2" />
                Find and replace
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowExport(true)} data-testid="menu-export">
                <Download className="w-4 h-4 mr-2" />
                Export file
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Full width</span>
                  <Switch
                    checked={isFullWidth}
                    onCheckedChange={handleFullWidthToggle}
                    data-testid="full-width-toggle"
                  />
                </div>
                {!readOnly && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm">Feedback panel</span>
                    <Switch
                      checked={showFeedbackPanel}
                      onCheckedChange={handleShowFeedbackToggle}
                      data-testid="feedback-panel-toggle"
                    />
                  </div>
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Center: Title */}
        <div className="flex-1 mx-4 max-w-2xl">
          {readOnly ? (
            <h1 className="text-xl font-bold font-[Manrope] text-slate-900 dark:text-white truncate">
              {title}
            </h1>
          ) : (
            <Input
              ref={titleInputRef}
              value={title}
              onChange={handleTitleChange}
              placeholder="Document title"
              className="text-xl font-bold font-[Manrope] border-0 p-0 h-auto focus-visible:ring-0 bg-transparent text-slate-900 dark:text-white"
              data-testid="document-title-input"
            />
          )}
        </div>

        {/* Right: Save + Comments */}
        <div className="flex items-center gap-2">
          {currentOrg?.id && !readOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-blue-200 text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-950/40 font-medium"
                  data-testid="open-ai-write-dropdown"
                  title="AI Write"
                >
                  <Wand2 className="w-4 h-4 mr-1 shrink-0" />
                  AI Write
                  <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-70 shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs font-normal text-slate-500 dark:text-slate-400">
                  Document Actions
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => openAiModal('write')} data-testid="ai-action-write">
                  <Sparkles className="w-4 h-4 mr-2 text-blue-600" />
                  Write with AI
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openAiModal('summary')} data-testid="ai-action-summary">
                  <Sparkles className="w-4 h-4 mr-2 text-slate-500" />
                  Generate summary
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openAiModal('grammar')} data-testid="ai-action-grammar">
                  <SpellCheck2 className="w-4 h-4 mr-2 text-slate-500" />
                  Fix grammar and spelling
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openAiModal('shorten')} data-testid="ai-action-shorten">
                  <Minus className="w-4 h-4 mr-2 text-slate-500" />
                  Make it shorter
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {/* Comments Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (showComments) {
                closeCommentsPanel();
              } else {
                setShowComments(true);
              }
            }}
            className={cn(showComments && "bg-blue-50 border-blue-200")}
            data-testid="toggle-comments-button"
          >
            <MessageSquare className="w-4 h-4 mr-1" />
            {inlineComments.length > 0 && (
              <span className="ml-1 bg-blue-600 text-white text-xs px-1.5 rounded-full">
                {inlineComments.length}
              </span>
            )}
          </Button>

          {/* Save Status & Button */}
          {!readOnly && (
            <>
              {saving ? (
                <span className="flex items-center text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </span>
              ) : lastSaved ? (
                <span className="flex items-center text-sm text-emerald-600 dark:text-emerald-400">
                  <Check className="w-4 h-4 mr-1" />
                  Saved
                </span>
              ) : hasChanges ? (
                <span className="text-sm text-amber-600 dark:text-amber-400">
                  Unsaved changes
                </span>
              ) : null}
              <Button
                onClick={() => handleSave(null, title)}
                disabled={!hasChanges || saving}
                className={cn(
                  "bg-blue-600 hover:bg-blue-700 text-white",
                  !hasChanges && "opacity-50"
                )}
                data-testid="save-document-button"
              >
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Color / comment popovers opened from Editor.js inline toolbar */}
      {inlineExtraPanel && !readOnly && selectedText && (
        <div
          className="fixed z-[100] max-w-[calc(100vw-16px)] bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 p-3 space-y-3"
          style={{
            top: inlineExtraPanel.top,
            left: inlineExtraPanel.left,
            width: inlineExtraPanel.mode === 'color' ? 300 : 320,
          }}
          data-testid="selection-toolbar"
          onMouseDown={(e) => {
            /* Blur prevention on the editor only for non-interactive hits; preventDefault on textarea/button would block focus and feel “disabled”. */
            const t = e.target;
            if (t instanceof Element && t.closest('textarea, input, button, select, label')) {
              return;
            }
            e.preventDefault();
          }}
        >
          {inlineExtraPanel.mode === 'color' && (
            <>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                  <Palette className="w-3.5 h-3.5" />
                  Text color
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={closeInlineExtraPanel}
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <ColorPicker
                onColorSelect={applyTextColor}
                onClose={closeInlineExtraPanel}
                closeOnPick={false}
              />
            </>
          )}
          {inlineExtraPanel.mode === 'comment' && (
            <>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                  <MessageSquare className="w-3.5 h-3.5" />
                  Comment
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={closeInlineExtraPanel}
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 italic line-clamp-2">"{selectedText}"</p>
              <textarea
                value={newCommentText}
                onChange={(e) => setNewCommentText(e.target.value)}
                placeholder="Add a comment (save document to persist in the page)…"
                className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900"
                rows={3}
                data-testid="comment-textarea"
              />
              <div className="flex justify-between gap-2">
                <Button variant="ghost" size="sm" className="text-slate-500" onClick={closeInlineExtraPanel}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={addInlineComment}
                  disabled={!newCommentText.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="submit-comment-button"
                >
                  Add comment
                </Button>
              </div>
              <p className="text-[10px] text-slate-400 leading-snug">
                Comments must stay inside one paragraph or heading. Save the document to keep the highlight.
              </p>
            </>
          )}
        </div>
      )}

      {/* Editor Container */}
      <div className="flex-1 overflow-auto">
        <div className={cn(
          "px-6 py-8 transition-all",
          isFullWidth ? "pl-[72px]" : "max-w-4xl mx-auto"
        )}>
          <div
            id={holderId}
            className={cn(
              "editorjs-container prose dark:prose-invert max-w-none",
              isFullWidth && "full-width"
            )}
            data-testid="editorjs-container"
          />
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>
            Last updated: {new Date(document.updated_at).toLocaleString()}
          </span>
          {!readOnly && (
            <span className="text-slate-400">
              Press Ctrl+S to save • Ctrl+F to find
            </span>
          )}
        </div>
      </div>

      {/* Modals */}
      <DocumentInfoModal
        isOpen={showDocInfo}
        onClose={() => setShowDocInfo(false)}
        document={document}
        space={space}
      />

      <PingTeamModal
        isOpen={showPingTeam}
        onClose={() => setShowPingTeam(false)}
        document={document}
        members={members}
        currentOrg={currentOrg}
      />

      <ShareDocumentModal
        isOpen={showShare}
        onClose={() => setShowShare(false)}
        document={document}
        space={space}
        publishSettings={publishSettings}
        onPublishChange={onPublishChange}
      />

      <FindReplaceModal
        isOpen={showFindReplace}
        onClose={() => setShowFindReplace(false)}
        document={document}
        onReplace={handleFindReplace}
      />

      <ExportDocumentModal
        isOpen={showExport}
        onClose={() => setShowExport(false)}
        document={document}
      />

      <EditorAiAssistantModal
        isOpen={showAiAssistant}
        onClose={() => setShowAiAssistant(false)}
        mode={aiModalMode}
        organizationId={currentOrg?.id}
        documentTitle={title}
        getFullDocumentText={getFullDocumentPlainText}
        getSelectionText={getSelectionPlainText}
        onInsertAtEnd={insertAiPlainTextAtEnd}
        onInsertSummaryCallout={insertAiSummaryCallout}
      />

      {/* Inline Comments Panel */}
      <InlineCommentsPanel
        isOpen={showComments}
        onClose={closeCommentsPanel}
        documentId={document.id}
        comments={inlineComments}
        onCommentsChange={setInlineComments}
        onHighlightComment={highlightCommentText}
        onCommentDeleted={handleCommentDeletedFromEditor}
        currentUserId={currentUser?.id}
        focusedCommentId={focusedSidebarCommentId}
      />
    </div>
  );
};

export default BlockEditor;
